"use client";

import { useEffect, useRef, useState, type KeyboardEvent } from "react";

import { PatientReview } from "@/components/reviews/patient-review";
import type { GoogleReview } from "@/lib/google-reviews";

// One review per card is tabbable (roving tabindex) so Tab moves card to card;
// arrow keys move between the reviews within the focused card.
export function DoctorReviewList({ reviews }: { reviews: GoogleReview[] }) {
  const [active, setActive] = useState(0);
  const [showFade, setShowFade] = useState(false);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const scrollerRef = useRef<HTMLDivElement>(null);

  // Fade the bottom edge while there's more to scroll; hide it at the end.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const update = () => {
      const overflow = el.scrollHeight > el.clientHeight + 4;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
      setShowFade(overflow && !atBottom);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [reviews.length]);

  const focusItem = (index: number) => {
    const next = Math.max(0, Math.min(index, reviews.length - 1));
    setActive(next);
    itemRefs.current[next]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    switch (e.key) {
      case "ArrowDown":
      case "ArrowRight":
        e.preventDefault();
        focusItem(active + 1);
        break;
      case "ArrowUp":
      case "ArrowLeft":
        e.preventDefault();
        focusItem(active - 1);
        break;
      case "Home":
        e.preventDefault();
        focusItem(0);
        break;
      case "End":
        e.preventDefault();
        focusItem(reviews.length - 1);
        break;
    }
  };

  return (
    <div className="relative mt-5 min-h-0 flex-1">
      <div
        ref={scrollerRef}
        className="scrollbar-none -mx-2 h-full overflow-y-auto overscroll-contain px-2 py-2"
      >
        <ul className="flex flex-col gap-5" onKeyDown={onKeyDown}>
          {reviews.map((review, i) => (
            <li key={review.uuid ?? i}>
              <PatientReview
                review={review}
                tabIndex={i === 0 ? 0 : -1}
                onFocus={() => setActive(i)}
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
              />
            </li>
          ))}
        </ul>
      </div>
      <div
        aria-hidden
        className={`from-dusk-01 pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-linear-to-t to-transparent transition-opacity duration-200 ${
          showFade ? "opacity-100" : "opacity-0"
        }`}
      />
    </div>
  );
}
